import {
    type UserDefinedObjectTemplate,
    UserDefinedObjectTemplateStatus,
} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplate";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    type OttopilotEventTemplateConfiguration,
    ottopilotEventTemplateConfigurationValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplateConfiguration";
import {z as zod} from "zod";

/** Domain view referencing UDO metadata and capability business configuration/parent relationship. */
export type OttopilotEventTemplate = {
    configuration: OttopilotEventTemplateConfiguration;
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    description: string | null;
    displayName: string;
    eventContainerTemplateId: Uuid;
    id: Uuid;
    status: OttopilotEventTemplateStatus;
    udoTemplateId: Uuid;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
};

export enum OttopilotEventTemplateStatus {
    active = "e4f3f399-3ef0-4f9d-8d7b-9cecf9f9a3a3",
    archived = "5a7bde5c-d5e0-41c2-a932-da8931f07d05",
}

export const ottopilotEventTemplateValidator: zod.ZodType<OttopilotEventTemplate> = zod.strictObject({
    configuration: ottopilotEventTemplateConfigurationValidator,
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    description: zod.string().nullable(),
    displayName: zod.string(),
    eventContainerTemplateId: uuidZodValidator,
    id: uuidZodValidator,
    status: zod.enum(OttopilotEventTemplateStatus),
    udoTemplateId: uuidZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
});

export type OttopilotEventTemplateCudLog = OttopilotEventTemplate;

export const ottopilotEventTemplateCudLogValidator: zod.ZodType<OttopilotEventTemplateCudLog> = ottopilotEventTemplateValidator;

/** Compose all configured templates, including archived ones, from validated capability and UDO objects.
 * Names, descriptions, statuses and audit fields come from the linked UDO template.
 * Missing bindings or UDO metadata are errors; callers must supply archived UDO templates too.
 */
export function getOttopilotEventTemplatesFromWorkspaceCapability({allUdoTemplates, workspaceCapability}: {
    allUdoTemplates: ReadonlyArray<UserDefinedObjectTemplate>;
    workspaceCapability: WorkspaceCapability<WorkspaceCapabilityType.ottopilotEvents>;
}): Array<OttopilotEventTemplate> {
    const entries = workspaceCapability.configuration.eventTemplates;
    const bindings = workspaceCapability.udoBindings.eventUdoTemplates;
    const workspaceTemplates = new Map(allUdoTemplates
        .filter((template) => template.workspaceId === workspaceCapability.workspaceId)
        .map((template) => [template.id, template]));

    for (const templateId of Object.keys(bindings)) {
        if (entries[uuidZodValidator.parse(templateId)] === undefined) {
            throw new Error(`OttopilotEvent template ${templateId} has a UDO binding but no business configuration`);
        }
    }

    return Object.entries(entries).map(([templateId, entry]): OttopilotEventTemplate => {
        const id = uuidZodValidator.parse(templateId);
        const binding = bindings[id];
        if (binding === undefined) {
            throw new Error(`OttopilotEvent template ${id} has no UDO binding`);
        }
        const udoTemplate = workspaceTemplates.get(binding.udoTemplateId);
        if (udoTemplate === undefined) {
            throw new Error(`UDO template ${binding.udoTemplateId} for OttopilotEvent template ${id} was not found in workspace ${workspaceCapability.workspaceId}`);
        }

        return {
            configuration: entry.configuration,
            createdAt: udoTemplate.createdAt,
            createdBy: udoTemplate.createdBy,
            description: udoTemplate.description,
            displayName: udoTemplate.displayName,
            eventContainerTemplateId: entry.eventContainerTemplateId,
            id,
            status: udoTemplate.status === UserDefinedObjectTemplateStatus.active
                ? OttopilotEventTemplateStatus.active
                : OttopilotEventTemplateStatus.archived,
            udoTemplateId: udoTemplate.id,
            updatedAt: udoTemplate.updatedAt,
            updatedBy: udoTemplate.updatedBy,
        };
    });
}
